<script setup lang="ts">
import { ref, watch, computed, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useCreateSensorStore } from '@/store/createSensor'
import { useErrorStore } from '@/store/error'
import { initialFormData, ISensorFormData } from '@/interfaces/components/createSensor/create-sensor.ts'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import SensorStepper from '@/components/Sensors/Create/SensorStepper.vue' 
import StepBasicInfo from '@/components/Sensors/Create/Steps/StepBasicInfo.vue'
import StepIdentification from '@/components/Sensors/Create/Steps/StepIdentification.vue'
import StepConnection from '@/components/Sensors/Create/Steps/StepConnection.vue'
import StepSchema from '@/components/Sensors/Create/Steps/SchemaStep/StepSchema.vue'
import StepConfirmation from '@/components/Sensors/Create/Steps/StepConfirmation.vue'

const router = useRouter() 

const createStore = useCreateSensorStore()
const { isCreatingNewSchema, draftSchemaPayload, suggestedSchemas, selectedSchema, connectionStatus } = storeToRefs(createStore)
const { cancelCreatingSchema, selectSchema, analyzePayloadForSchemas } = createStore

const stepsList = ['Tipo e fabricante', 'Identificação', 'Conexão', 'Definição de modelo', 'Confirmação']
const totalSteps = stepsList.length

const currentStep = ref(1)
const isStepValid = ref(false) 

const formData = ref<ISensorFormData>({ ...initialFormData })

onUnmounted(() => {
  createStore.resetState()
})

const handleBack = () => {
  if (currentStep.value === 4 && isCreatingNewSchema.value && suggestedSchemas.value.length > 0) {
    cancelCreatingSchema()
    return
  }
  if (currentStep.value > 1) {
    currentStep.value--
  } else {
    router.back()
  }
}

const handleNext = async () => {
  if (currentStep.value === 3) {
    if (connectionStatus.value !== 'success') {
      alert("Por favor, realize o teste de conexão com sucesso antes de avançar.")
      return
    }
    try{
      await analyzePayloadForSchemas(formData.value.sensor_type)
      await nextTick()
      currentStep.value++
      isStepValid.value = false 
      return
    } catch (error) {
      console.error("Erro ao analisar payload para sugestões de schemas:", error)
      return
    }
  }

  if (currentStep.value === 4) {
    if (isCreatingNewSchema.value) {
      const payload = draftSchemaPayload.value
      if (!payload?.name?.trim() || !payload?.schema || Object.keys(payload.schema).length === 0) {
        return
      }
      selectSchema(payload)
      formData.value.schema_sensor_id = null
      formData.value.schema_name = payload.name
      formData.value.is_public_schema = payload.is_public_schema ?? false
      formData.value.schema = payload.schema
    } else {
      if (!selectedSchema.value) return
      formData.value.schema_sensor_id = selectedSchema.value.id
      formData.value.schema_name = ''
      formData.value.schema = undefined
    }
    currentStep.value++
    
    isStepValid.value = true 
    return
  }

  if (currentStep.value < totalSteps) {
    currentStep.value++ 
    if (currentStep.value <= 4) isStepValid.value = false 
    else isStepValid.value = true 
  } else {
    finishProcess()
  }
}

const buildCreateSensorPayload = () => {
  const data = formData.value
  const payload: Record<string, unknown> = {
    sensor_name: data.sensor_name,
    mac_address: data.mac_address,
    subid: data.subid,
    sensor_type: data.sensor_type,
    manufacturer: data.manufacturer,
    is_public_schema: data.is_public_schema,
  }
  if (data.schema_sensor_id) {
    payload.schema_sensor_id = data.schema_sensor_id
  } else {
    payload.schema_name = data.schema_name
    payload.schema = data.schema && Object.keys(data.schema).length > 0 ? data.schema : undefined
  }
  return payload
}

const finishProcess = async () => {
  const data = formData.value
  if (!data.schema_sensor_id) {
    const hasSchemaName = data.schema_name?.trim?.()
    const hasSchema = data.schema && Object.keys(data.schema).length > 0
    if (!hasSchemaName || !hasSchema) {
      useErrorStore().setErrors('erro', ['Preencha o nome do modelo e pelo menos um mapeamento no schema.'])
      return
    }
  }
  try {
    const payload = buildCreateSensorPayload()
    await createStore.createSensor(payload)
    router.push({ name: 'list-sensors' })
  } catch (error) {
    console.error("Erro ao criar sensor:", error)
    return
  }
}

const handleStepValidate = (isValid: boolean) => {
  isStepValid.value = isValid
}

const backLabel = computed(() => {
  if (currentStep.value === 4 && isCreatingNewSchema.value && suggestedSchemas.value.length > 0) return 'Cancelar'
  return currentStep.value === 1 ? 'Cancelar' : 'Voltar'
})

const nextLabel = computed(() => {
  if (currentStep.value === 4 && isCreatingNewSchema.value) return 'Salvar Modelo'
  return currentStep.value === totalSteps ? 'Finalizar' : 'Avançar'
})
</script>

<template>
  <DefaultLayout>
    <div class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        :to="{ name: 'list-sensors' }"
        title="Criar Sensor"
        subtitle="Cadastre um novo sensor em 5 etapas: tipo, identificação, conexão, modelo de dados e confirmação."
      />

      <div class="flex flex-col bg-white shadow-sm dark:bg-boxdark min-h-[500px]">
        <div class="border-b border-stroke px-4 py-6 sm:px-6 dark:border-strokedark">
          <SensorStepper :current-step="currentStep" :steps="stepsList" />
        </div>

        <div class="flex-1 p-4 sm:p-6">
          <div class="max-w-4xl mx-auto">
            <transition name="fade" mode="out-in">
              <div
                :class="[
                  'h-full',
                  currentStep === 3 && 'rounded-lg border border-stroke dark:border-strokedark overflow-hidden'
                ]"
              >
                <StepBasicInfo 
                  v-if="currentStep === 1" 
                  v-model="formData" 
                  @validate="handleStepValidate" 
                />
                <StepIdentification 
                  v-else-if="currentStep === 2" 
                  v-model="formData" 
                  @validate="handleStepValidate" 
                />
                <StepConnection 
                  v-else-if="currentStep === 3" 
                  v-model="formData" 
                  @validate="handleStepValidate" 
                />
                <StepSchema 
                  v-else-if="currentStep === 4" 
                  v-model="formData" 
                  @validate="handleStepValidate" 
                />
                <StepConfirmation
                  v-else
                  :form-data="formData"
                  :selected-schema="selectedSchema"
                />
              </div>
            </transition>
          </div>
        </div>

        <div class="flex items-center justify-between border-t border-stroke px-4 py-4 sm:px-6 dark:border-strokedark">
          <button
            class="flex items-center justify-center gap-2 border border-stroke px-6 py-2 font-medium text-slate-700 transition hover:bg-slate-50 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
            type="button"
            @click="handleBack"
          >
            {{ backLabel }}
          </button>
          <button 
            class="flex justify-center rounded px-6 py-2 font-medium transition-all min-w-[120px]" 
            :class="!isStepValid ? 'bg-slate-300 cursor-not-allowed text-slate-500' : 'bg-[#1d4ed8] hover:bg-[#1e40af] text-white'"
            type="button"
            :disabled="!isStepValid" 
            @click="handleNext"
          >
            {{ nextLabel }}
          </button>
        </div>
      </div>
    </div>
  </DefaultLayout>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>